/** Médias móveis de quadro para o HUD (pura, testável). */
export class FrameTimer {
  private readonly samples: number[] = [];
  private readonly capacity: number;

  constructor(capacity = 120) {
    this.capacity = capacity;
  }

  push(ms: number): void {
    this.samples.push(ms);
    if (this.samples.length > this.capacity) this.samples.shift();
  }

  get averageMs(): number {
    if (this.samples.length === 0) return 0;
    return this.samples.reduce((a, b) => a + b, 0) / this.samples.length;
  }

  get fps(): number {
    const avg = this.averageMs;
    return avg > 0 ? 1000 / avg : 0;
  }

  /** Pior quadro da janela (picos importam mais que a média). */
  get worstMs(): number {
    return this.samples.length === 0 ? 0 : Math.max(...this.samples);
  }
}

/** Percentis de uma série de tempos (ms), pelo posto mais próximo. */
export interface Percentiles {
  p50: number;
  p95: number;
  p99: number;
}

export interface PerfSummary {
  frames: number;
  seconds: number;
  /** JavaScript por quadro (a função `frame` inteira, com a submissão do render). */
  meanMs: number;
  maxMs: number;
  /** Draw calls da cena principal (o teto de 250 do doc 05). */
  maxCalls: number;
  /** Draw calls do quadro inteiro (F52): ícones, cena e pós-processamento. */
  maxFrameCalls: number;
  maxTriangles: number;
  /** Percentis do JavaScript (Fase 12, Gate A). */
  js: Percentiles;
  /** Intervalo entre quadros (o que se vê): percentis, pior e engasgos acima de `HITCH_MS`. */
  frame: Percentiles & { maxMs: number; hitches: number };
  /** Tempo de GPU por quadro (`GpuTimer`), só onde a extensão existe; `undefined` sem amostras. */
  gpu: (Percentiles & { meanMs: number; samples: number }) | undefined;
}

/** Um quadro acima disto (dois quadros de 60 Hz) conta como engasgo. */
export const HITCH_MS = 33.4;
/** Teto de amostras guardadas (cerca de 30 min a 60 Hz); acima, só médias e máximos seguem. */
const MAX_SAMPLES = 108_000;

/** Percentis pelo posto mais próximo; zero sem amostras. */
export function percentiles(samples: readonly number[]): Percentiles {
  if (samples.length === 0) return { p50: 0, p95: 0, p99: 0 };
  const sorted = [...samples].sort((a, b) => a - b);
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(q * sorted.length) - 1))]!;
  return { p50: at(0.5), p95: at(0.95), p99: at(0.99) };
}

/**
 * Janela de medição explícita (F36): todos os quadros desde `reset`, com a média e os máximos do
 * JavaScript, dos draw calls (da cena e, F52, do quadro inteiro) e dos triângulos (a média móvel do
 * HUD esquece os picos antigos).
 */
export class PerfWindow {
  private frames = 0;
  private sumMs = 0;
  private maxMs = 0;
  private maxCalls = 0;
  private maxFrameCalls = 0;
  private maxTriangles = 0;
  private started = 0;
  private readonly jsSamples: number[] = [];
  private readonly intervals: number[] = [];
  private maxInterval = 0;
  private hitches = 0;
  private readonly gpuSamples: number[] = [];
  private gpuSum = 0;
  /** Todas as medidas de GPU (a média), mesmo acima do teto das amostras guardadas (os percentis). */
  private gpuCount = 0;

  reset(now: number): void {
    this.frames = 0;
    this.sumMs = 0;
    this.maxMs = 0;
    this.maxCalls = 0;
    this.maxFrameCalls = 0;
    this.maxTriangles = 0;
    this.started = now;
    this.jsSamples.length = 0;
    this.intervals.length = 0;
    this.maxInterval = 0;
    this.hitches = 0;
    this.gpuSamples.length = 0;
    this.gpuSum = 0;
    this.gpuCount = 0;
  }

  /** Um quadro: o JavaScript (`ms`), as contagens e, se houver, o intervalo desde o anterior. */
  add(ms: number, calls: number, triangles: number, frameCalls = calls, intervalMs?: number): void {
    this.frames++;
    this.sumMs += ms;
    this.maxMs = Math.max(this.maxMs, ms);
    this.maxCalls = Math.max(this.maxCalls, calls);
    this.maxFrameCalls = Math.max(this.maxFrameCalls, frameCalls);
    this.maxTriangles = Math.max(this.maxTriangles, triangles);
    if (this.jsSamples.length < MAX_SAMPLES) this.jsSamples.push(ms);
    if (intervalMs === undefined) return;
    this.maxInterval = Math.max(this.maxInterval, intervalMs);
    if (intervalMs > HITCH_MS) this.hitches++;
    if (this.intervals.length < MAX_SAMPLES) this.intervals.push(intervalMs);
  }

  /** Um resultado do `GpuTimer` (chega alguns quadros depois do quadro medido). */
  addGpu(ms: number): void {
    this.gpuSum += ms;
    this.gpuCount++;
    if (this.gpuSamples.length < MAX_SAMPLES) this.gpuSamples.push(ms);
  }

  summary(now: number): PerfSummary {
    const gpu = this.gpuCount ? { ...percentiles(this.gpuSamples), meanMs: this.gpuSum / this.gpuCount, samples: this.gpuCount } : undefined;
    return {
      frames: this.frames,
      seconds: (now - this.started) / 1000,
      meanMs: this.frames ? this.sumMs / this.frames : 0,
      maxMs: this.maxMs,
      maxCalls: this.maxCalls,
      maxFrameCalls: this.maxFrameCalls,
      maxTriangles: this.maxTriangles,
      js: percentiles(this.jsSamples),
      frame: { ...percentiles(this.intervals), maxMs: this.maxInterval, hitches: this.hitches },
      gpu,
    };
  }
}

/** Passo de tempo limitado: após uma aba em segundo plano, não "salta" segundos de simulação. */
export function clampDelta(seconds: number, max = 0.1): number {
  return seconds < 0 ? 0 : seconds > max ? max : seconds;
}
